import { useEffect, forwardRef } from "react";
import styled from "styled-components";
import { Flex, Box, Text } from "rebass/styled-components";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { NextUpEmptyState } from "../NextUpEmptyState";
import { WatchLimitBlocker } from "../WatchLimitBlocker";
import { NextUpEmbeddablePlaylistViewModel } from "../../ViewModel";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { useWatchLimit } from "MVVM/Hooks/useWatchLimit";
import {
  NextUpPlaylistState,
  ActivePlaylistClipState,
  MatchTypeState,
  FallbackPlaylistState,
  PlaylistClipsState,
  ShowWatchLimitBlockerState,
  BlockedPlayerNameState,
  BlockedClipState,
} from "MVVM/State/embeddablePlaylist";
import { formatRelativeTime } from "MVVM/Utilities/Time";
import EmbeddablePlayer from "MVVM/Views/EmbeddablePlayer";

export const NextUpPlayer = forwardRef<
  HTMLDivElement,
  {
    platform: string;
    playlist: string;
    primaryColor?: string;
    secondaryColor?: string;
  }
>(
  (
    {
      platform,
      playlist,
      primaryColor = "#3273FA",
      secondaryColor = "#1b181f",
    },
    ref,
  ) => {
    const setIsNextUpPlaylist = useSetRecoilState(NextUpPlaylistState);
    const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
    const matchType = useRecoilValue(MatchTypeState);
    const isFallbackPlaylist = useRecoilValue(FallbackPlaylistState);
    const playlistClips = useRecoilValue(PlaylistClipsState);
    const showWatchLimitBlocker = useRecoilValue(ShowWatchLimitBlockerState);
    const blockedPlayerName = useRecoilValue(BlockedPlayerNameState);
    const blockedClip = useRecoilValue(BlockedClipState);

    const { handleCloseBlocker } = useWatchLimit();

    const currentClip =
      playlistClips?.find((clip) => clip._id === activePlaylistClip?.id) ||
      playlistClips?.[0];
    const gamePlayed =
      currentClip?.metadata
        ?.find((meta) => meta.key === "Game Played")
        ?.value?.toString() || "";
    const matchDate = formatRelativeTime(
      gamePlayed || currentClip?.createdDate || "",
    );
    const cleanMatchType = matchType?.replace(/ games?$/i, "") || "";

    useEffect(() => {
      setIsNextUpPlaylist(true);
    }, [setIsNextUpPlaylist]);

    NextUpEmbeddablePlaylistViewModel({ platform });

    const isOPGG = platform === "OPGG" || platform === "OP.GG";

    if (isFallbackPlaylist) return <NextUpEmptyState />;

    return (
      <StyledContainer flexDirection="column">
        <PlayerContainer ref={ref}>
          {activePlaylistClip ? (
            <EmbeddablePlayer
              className="next-up-player"
              onPlayEndVariant="nextUp"
            />
          ) : (
            <PlayerLoadingSkeleton>
              <LoadingSkeleton width="100%" height="100%" />
            </PlayerLoadingSkeleton>
          )}
          {showWatchLimitBlocker && (
            <WatchLimitBlocker
              onClose={handleCloseBlocker}
              playerName={blockedPlayerName}
              clickedClip={blockedClip}
              playlistId={playlist}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
            />
          )}
        </PlayerContainer>
        <StyledCopyContainer
          className="next-up-copy-container"
          $isOPGG={isOPGG}
        >
          {activePlaylistClip ? (
            <>
              <StyledClipTitle>{activePlaylistClip?.title}</StyledClipTitle>
              <Flex>
                <StyledClipDetails>{`${activePlaylistClip?.username} - ${cleanMatchType}, ${matchDate}`}</StyledClipDetails>
              </Flex>
            </>
          ) : (
            <PlayerLoadingSkeleton>
              <LoadingSkeleton width="35%" />
            </PlayerLoadingSkeleton>
          )}
        </StyledCopyContainer>
      </StyledContainer>
    );
  },
);

const StyledClipTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.white};
  font-size: 12px;
  font-weight: 700;
`;

const StyledClipDetails = styled(Text)`
  color: #cddcfe;
  font-size: 10px;
  font-weight: 500;
`;

const StyledContainer = styled(Flex)`
  container-type: inline-size;
  container-name: next-up-container;

  height: 100%;
  position: relative;
  width: 100%;
`;

const PlayerContainer = styled(Box)`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  height: 100%;

  .next-up-player {
    height: 100%;
    width: 100%;
  }

  .next-up-player .embeddable-player {
    height: 100% !important;
  }

  .next-up-player .video-js {
    height: 100% !important;
    width: 100% !important;
  }

  .next-up-player [data-vjs-player] {
    height: 100% !important;
  }

  .next-up-player [data-vjs-player] > div {
    height: 100% !important;
  }

  .next-up-player > .overlay {
    height: calc(100% - 54px) !important;
  }

  .next-up-player .vjs-control-bar {
    z-index: 10 !important;
  }

  .vjs-tech {
    border-radius: 8px;
    height: 100% !important;
    width: 100% !important;
  }
`;

const PlayerLoadingSkeleton = styled(Box)`
  border-radius: 8px;
  height: 100%;
  width: 100%;
`;

const StyledCopyContainer = styled(Flex)<{ $isOPGG: boolean }>`
  background: ${({ $isOPGG }) => ($isOPGG ? "#31313C" : "#191937")};
  height: 54px;
  flex-direction: column;
  flex-shrink: 0;
  gap: 5px;
  padding: 10px;
`;
